import { useEffect, useRef, useState } from "react";
import { liveWsUrl } from "../auth-config";
import type { CohortEventRecord } from "../auth/api-client";

type LiveStatus = "off" | "connecting" | "live" | "unavailable";

interface LiveMessage {
  type: string;
  event?: CohortEventRecord;
}

export function useTeacherLiveActivity(opts: {
  bearer: string | null;
  cohortId: string;
  onEvent: (event: CohortEventRecord) => void;
}): LiveStatus {
  const { bearer, cohortId, onEvent } = opts;
  const [status, setStatus] = useState<LiveStatus>("off");
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;

  useEffect(() => {
    const base = liveWsUrl.replace(/\/$/, "");
    if (!bearer || !base) {
      setStatus("unavailable");
      return;
    }

    let cancelled = false;
    let socket: WebSocket | null = null;
    let retryMs = 1000;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;

    const subscribe = () => {
      if (!socket || socket.readyState !== WebSocket.OPEN) return;
      socket.send(
        JSON.stringify({
          action: "subscribe",
          cohortId: cohortId || null,
        })
      );
    };

    const connect = () => {
      if (cancelled) return;
      setStatus("connecting");
      const qs = new URLSearchParams({ token: bearer });
      if (cohortId) qs.set("cohortId", cohortId);
      socket = new WebSocket(`${base}?${qs.toString()}`);

      socket.addEventListener("open", () => {
        if (cancelled) return;
        retryMs = 1000;
        setStatus("live");
        subscribe();
      });

      socket.addEventListener("message", (ev) => {
        if (cancelled || typeof ev.data !== "string") return;
        try {
          const msg = JSON.parse(ev.data) as LiveMessage;
          if (msg.type === "cohort_activity" && msg.event?.id) {
            onEventRef.current(msg.event);
          }
        } catch {
          // ignore malformed frames
        }
      });

      socket.addEventListener("close", () => {
        if (cancelled) return;
        setStatus("connecting");
        retryTimer = setTimeout(() => {
          retryMs = Math.min(retryMs * 2, 30_000);
          connect();
        }, retryMs);
      });

      socket.addEventListener("error", () => {
        socket?.close();
      });
    };

    connect();

    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
      socket?.close();
      setStatus("off");
    };
  }, [bearer, cohortId]);

  return status;
}
